/**
 * Frequency Role - Cast Pillar
 *
 * Normaliza input sujo para número, com detecção automática de unidade.
 *
 * @example
 * import { castFrequency, castFrequencyStrict, detectFrequencyUnit } from '@attrx/role-morphic/frequency/cast';
 *
 * castFrequency('2.4 GHz');                    // 2400000000 (converted to hertz)
 * castFrequency('2.4 GHz', 'gigahertz');       // 2.4 (kept as gigahertz)
 * castFrequencyStrict('100');                  // 100 (no conversion)
 * detectFrequencyUnit('3600 rpm');             // 'rpm'
 */

import { FREQUENCY_UNITS, FREQUENCY_ALIASES, type FrequencyUnit } from "./constants";
import { convertFrequency, FREQUENCY_BASE } from "./convert";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> = { ok: true; value: T } | { ok: false; error: string };

// =============================================================================
// CAST
// =============================================================================

/**
 * Faz cast de input para número, com conversão opcional para unidade alvo
 *
 * @param input - Valor de entrada (number, string, etc)
 * @param targetVariant - Unidade alvo (default: hertz)
 * @returns Número ou null se não puder converter
 *
 * @example
 * castFrequency('2.4 GHz');               // 2400000000 (hertz)
 * castFrequency('2.4 GHz', 'gigahertz');  // 2.4
 * castFrequency('100');                   // 100
 * castFrequency(100);                     // 100
 * castFrequency('invalid');               // null
 */
export function castFrequency(
  input: unknown,
  targetVariant: FrequencyUnit = FREQUENCY_BASE,
): number | null {
  // Already a number
  if (typeof input === "number") {
    return Number.isFinite(input) ? input : null;
  }

  // String parsing
  if (typeof input === "string") {
    const parsed = parseFrequencyString(input);
    if (parsed === null) {
      return null;
    }

    // If unit was detected and differs from target, convert
    if (parsed.unit && parsed.unit !== targetVariant) {
      return convertFrequency(parsed.unit, targetVariant, parsed.value);
    }

    return parsed.value;
  }

  return null;
}

/**
 * Faz cast sem conversão (apenas parseia o número)
 *
 * @param input - Valor de entrada
 * @returns Número ou null
 *
 * @example
 * castFrequencyStrict('100 MHz');  // 100 (ignora a unidade)
 * castFrequencyStrict('100');      // 100
 */
export function castFrequencyStrict(input: unknown): number | null {
  if (typeof input === "number") {
    return Number.isFinite(input) ? input : null;
  }

  if (typeof input === "string") {
    const parsed = parseFrequencyString(input);
    return parsed?.value ?? null;
  }

  return null;
}

/**
 * Versão safe de castFrequency que retorna Result
 */
export function tryCastFrequency(
  input: unknown,
  targetVariant: FrequencyUnit = FREQUENCY_BASE,
): Result<number> {
  const result = castFrequency(input, targetVariant);
  if (result === null) {
    return { ok: false, error: `Cannot cast "${String(input)}" to frequency` };
  }
  return { ok: true, value: result };
}

// =============================================================================
// DETECT UNIT
// =============================================================================

/**
 * Detecta a unidade de frequência de uma string
 *
 * @param input - String com valor e unidade
 * @returns Unidade detectada ou null
 *
 * @example
 * detectFrequencyUnit('100 MHz');    // 'megahertz'
 * detectFrequencyUnit('3600 rpm');   // 'rpm'
 * detectFrequencyUnit('100');        // null
 */
export function detectFrequencyUnit(input: string): FrequencyUnit | null {
  const trimmed = input.trim().toLowerCase();

  // Extract unit part (after the number)
  const match = trimmed.match(/^[+-]?[\d.,\s]+\s*(.*)$/);
  if (!match || !match[1]) {
    return null;
  }

  const unitStr = match[1].trim();
  if (!unitStr) {
    return null;
  }

  // Check direct unit names
  if (unitStr in FREQUENCY_UNITS) {
    return unitStr as FrequencyUnit;
  }

  // Check aliases
  if (unitStr in FREQUENCY_ALIASES) {
    return FREQUENCY_ALIASES[unitStr] as FrequencyUnit;
  }

  // Check symbols (case-sensitive for some)
  for (const [unit, config] of Object.entries(FREQUENCY_UNITS)) {
    if (config.symbol.toLowerCase() === unitStr) {
      return unit as FrequencyUnit;
    }
  }

  return null;
}

// =============================================================================
// INTERNAL HELPERS
// =============================================================================

interface ParsedFrequency {
  value: number;
  unit: FrequencyUnit | null;
}

/**
 * Parseia string de frequência extraindo valor e unidade
 */
function parseFrequencyString(input: string): ParsedFrequency | null {
  const trimmed = input.trim();
  if (!trimmed) {
    return null;
  }

  // Try to extract number and unit
  const match = trimmed.match(/^([+-]?[\d.,\s]+)\s*(.*)$/);
  if (!match) {
    return null;
  }

  const numStr = match[1].replace(/\s/g, "").replace(",", ".");
  const value = parseFloat(numStr);

  if (isNaN(value) || !Number.isFinite(value)) {
    return null;
  }

  const unitStr = match[2]?.trim().toLowerCase() || "";
  let unit: FrequencyUnit | null = null;

  if (unitStr) {
    // Check aliases first
    if (unitStr in FREQUENCY_ALIASES) {
      unit = FREQUENCY_ALIASES[unitStr] as FrequencyUnit;
    }
    // Check direct unit names
    else if (unitStr in FREQUENCY_UNITS) {
      unit = unitStr as FrequencyUnit;
    }
    // Check symbols
    else {
      for (const [u, config] of Object.entries(FREQUENCY_UNITS)) {
        if (config.symbol.toLowerCase() === unitStr) {
          unit = u as FrequencyUnit;
          break;
        }
      }
    }
  }

  return { value, unit };
}
